import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { IAction } from '../../shared-interfaces/Notation/IAction';
import { IMethodOfContact } from '../../shared-interfaces/Notation/IMethodOfContact';
import { INotation } from '../../shared-interfaces/Notation/INotation';
import { INotationComment } from '../../shared-interfaces/Notation/INotationComment';
import { ICoreUser } from '../../shared-interfaces/User/ICoreUser';
import AlertService from '../../shared-services/Notation/AlertService';
import DataInsightService from '../../shared-services/Notation/DataInsightService';
import UpdateNoteService from '../../shared-services/Notation/UpdateNoteService';
import { NotationType } from '../../shared-types/Notation/NotationType';
import { Role } from '../../shared-types/Roles';
import BeaconRte, {
  convertBeaconRteToHtml,
  EditorValueHasText,
  EmptyRteValue,
} from '../BeaconRte';
import NotationResponseAttributesContext from '../contexts/NotationResponseAttributesContext';
import DialogTransition from '../DialogTransition';
import UserSearch from '../UserSearch';

const useStyles = makeStyles({
  editor: {
    fontFamily: ['"Open Sans"', 'Helvetica', 'Arial', '"sans - serif"'].join(
      ',',
    ),
  },
});

function audienceMapper(audienceType: string) {
  switch (audienceType) {
    case 'All':
      return 'Professional Staff';
    case 'FullNetwork':
      return 'Full Network';
    case 'Private':
      return 'Private';
    case 'SpecificNetworkMembers':
      return 'Specific Network Members';
    default:
      return audienceType;
  }
}

export interface INotationResponseDialogV2 {
  open: boolean;
  close: () => void;
  notation: INotation;
  assignable?: boolean;
  additionalAudience?: ICoreUser[];
  assignedTo?: ICoreUser;
  title: string;
  usedToLower?: boolean;
  defaultText?: string;
  transition?: boolean;
  onComplete?: (
    success: boolean,
    comments?: INotationComment[],
    additionalAudience?: ICoreUser[],
    assignedTo?: ICoreUser,
  ) => void;
}

const NotationResponseDialogV2 = (props: INotationResponseDialogV2) => {
  const { actions, methodsOfContact } = React.useContext(
    NotationResponseAttributesContext,
  );

  const isLessThanSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const [assignedTo, setAssignedTo] = useState(props.notation.assignedTo);
  const [additionalAudience, setAdditionalAudience] = useState<
    ICoreUser[] | undefined
  >();
  const [initialAdditionalAudience, setInitialAdditionalAudience] = useState<
    ICoreUser[] | undefined
  >();

  const [action, setAction] = useState<IAction>();
  const [methodOfContact, setMethodOfContact] = useState<IMethodOfContact>();
  const [details, setDetails] = useState(EmptyRteValue());
  const [validAction, setValidAction] = useState<boolean>(true);
  const [validMethodOfContact, setValidMethodOfContact] =
    useState<boolean>(true);
  const [validComment, setValidComment] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (props.open) {
      setAdditionalAudience(props.additionalAudience);
      setInitialAdditionalAudience(props.additionalAudience);
    }
  }, [props.open]);

  useEffect(() => {
    setAssignedTo(props.notation.assignedTo);
  }, [props.notation]);

  useEffect(() => {
    if (action && action.id !== actions.find(x => x.name === 'Response')?.id) {
      setMethodOfContact(undefined);
    }
  }, [action, setMethodOfContact]);

  function resetState() {
    setAction(undefined);
    setMethodOfContact(undefined);
    setDetails(EmptyRteValue());
    setValidAction(true);
    setValidMethodOfContact(true);
    setValidComment(true);
    setIsSubmitting(false);
  }

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        props.close();
        setTimeout(() => {
          resetState();
        }, 500);
      }}
      aria-labelledby="notationResponseDialog"
      maxWidth="sm"
      fullScreen={isLessThanSm}
      fullWidth
      //@ts-ignore
      TransitionComponent={props.transition ? DialogTransition : undefined}
    >
      <DialogTitle id="notationResponseDialog">{props.title}</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={2}>
          {props.notation.audienceType && (
            <Grid item>
              <Typography>
                Audience: {audienceMapper(props.notation.audienceType)}
              </Typography>
            </Grid>
          )}
          {(props.notation.notationType === 'Alert' ||
            props.notation.notationType === 'Update') &&
            props.assignable && (
              <Grid item>
                <UserSearch
                  label="Additional Audience"
                  value={additionalAudience}
                  disabledValues={initialAdditionalAudience}
                  roleRestriction={[
                    Role.Generalist,
                    Role.Executive,
                    Role.Administrator,
                  ]}
                  onSelect={users =>
                    setAdditionalAudience(users as ICoreUser[])
                  }
                  multiple
                />
              </Grid>
            )}

          {props.notation.notationType === NotationType.Alert &&
            props.assignable && (
              <Grid item>
                {assignedTo && (
                  <>
                    <Typography
                      style={{ display: 'inline-block', paddingRight: '5px' }}
                    >
                      Assigned To:{' '}
                    </Typography>
                    <Chip
                      avatar={
                        <Avatar
                          alt={`${assignedTo.firstName} ${assignedTo.lastName}`}
                          src={
                            assignedTo.profileImageFilePath ??
                            assignedTo.institutionProfileImageFilePath
                          }
                        />
                      }
                      label={`${assignedTo.firstName} ${assignedTo.lastName}`}
                      onDelete={() => setAssignedTo(undefined)}
                    />
                  </>
                )}
                {!assignedTo && props.notation.student && (
                  <UserSearch
                    assignableTo={props.notation.student.id.toString()}
                    label="Assign To"
                    roleRestriction={[
                      Role.PeerAdvisor,
                      Role.Advisor,
                      Role.Generalist,
                      Role.Executive,
                      Role.Administrator,
                      Role.Support,
                    ]}
                    onSelect={user => setAssignedTo(user as ICoreUser)}
                  />
                )}
              </Grid>
            )}

          <Grid item>
            <FormControl
              required
              variant="outlined"
              style={{ width: '100%' }}
              error={!validAction && validAction !== undefined}
            >
              <InputLabel id="sn-action-label">Action</InputLabel>
              <Select
                labelId="sn-action-label"
                value={action ? action.id : ''}
                onChange={e => {
                  const action = actions.find(x => x.id === e.target.value);
                  setAction(action);
                  setValidAction(Boolean(action));
                }}
                label="Action"
              >
                <MenuItem value="">
                  <em>Select...</em>
                </MenuItem>
                {actions &&
                  actions.map((x: IAction) => (
                    <MenuItem key={x.id} value={x.id}>
                      {x.name}
                    </MenuItem>
                  ))}
              </Select>
              {!validAction && (
                <Typography variant="caption" color="error">
                  Action is required
                </Typography>
              )}
            </FormControl>
          </Grid>
          {action?.id === actions.find(x => x.name === 'Response')?.id && (
            <Grid item>
              <FormControl
                required
                variant="outlined"
                style={{ width: '100%' }}
                error={
                  !validMethodOfContact && validMethodOfContact !== undefined
                }
              >
                <InputLabel id="sn-response-type-label">
                  Response Type
                </InputLabel>
                <Select
                  labelId="sn-response-type-label"
                  value={methodOfContact ? methodOfContact.id : ''}
                  onChange={e => {
                    const methodOfContact = methodsOfContact.find(
                      x => x.id === e.target.value,
                    );
                    setMethodOfContact(methodOfContact);
                    setValidMethodOfContact(Boolean(methodOfContact));
                  }}
                  label="Response Type"
                >
                  <MenuItem value="">
                    <em>Select...</em>
                  </MenuItem>
                  {methodsOfContact &&
                    methodsOfContact.map((x: IMethodOfContact) => (
                      <MenuItem key={x.id} value={x.id}>
                        {x.name}
                      </MenuItem>
                    ))}
                </Select>
                {!validMethodOfContact && (
                  <Typography variant="caption" color="error">
                    Response Type is required
                  </Typography>
                )}
              </FormControl>
            </Grid>
          )}
          <Grid item>
            <BeaconRte
              label="Description"
              value={details}
              onChange={e => {
                setDetails(e);
              }}
              error={!validComment}
              required
            />
            {!validComment && (
              <Typography variant="caption" color="error">
                Description is required
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            props.close();
            setTimeout(() => {
              resetState();
            }, 500);
          }}
          color="primary"
          variant="text"
        >
          Cancel
        </Button>
        <Button
          disabled={isSubmitting}
          onClick={e => {
            setIsSubmitting(true);
            try {
              let valid = true;
              if (!action) {
                setValidAction(false);
                valid = false;
              }
              if (action?.name === 'Response' && !methodOfContact) {
                setValidMethodOfContact(false);
                valid = false;
              }
              if (details && !EditorValueHasText(details)) {
                setValidComment(false);
                valid = false;
              }
              if (valid) {
                if (props.notation.notationType === NotationType.Alert) {
                  if (props.usedToLower) {
                    AlertService.lower(
                      props.notation.id,
                      action?.id ?? 0,
                      convertBeaconRteToHtml(details),
                      methodOfContact?.id ?? undefined,
                      assignedTo?.id ?? undefined,
                    )
                      .then(() => {
                        AlertService.getComments(props.notation.id)
                          .then(c => {
                            c.sort(
                              (a, b) =>
                                b.createdOn.getTime() - a.createdOn.getTime(),
                            );
                            if (props.onComplete) props.onComplete(true, c);
                            props.close();
                          })
                          .finally(() => {
                            resetState();
                          });
                      })
                      .catch(() => {
                        if (props.onComplete) props.onComplete(false);
                      });
                  } else {
                    AlertService.addComment(
                      props.notation.id,
                      action as IAction,
                      convertBeaconRteToHtml(details),
                      assignedTo,
                      additionalAudience,
                      methodOfContact,
                    )
                      .then(() => {
                        AlertService.getComments(props.notation.id)
                          .then(c => {
                            c.sort(
                              (a, b) =>
                                b.createdOn.getTime() - a.createdOn.getTime(),
                            );
                            if (props.onComplete)
                              props.onComplete(
                                true,
                                c,
                                additionalAudience,
                                assignedTo,
                              );
                            props.close();
                          })
                          .finally(() => {
                            resetState();
                          });
                      })
                      .catch(() => {
                        if (props.onComplete) props.onComplete(false);
                      });
                  }
                } else if (
                  props.notation.notationType === NotationType.Update
                ) {
                  UpdateNoteService.addComment(
                    props.notation.id,
                    action as IAction,
                    convertBeaconRteToHtml(details),
                    assignedTo,
                    methodOfContact,
                  )
                    .then(() => {
                      UpdateNoteService.getComments(props.notation.id)
                        .then(c => {
                          c.sort(
                            (a, b) =>
                              b.createdOn.getTime() - a.createdOn.getTime(),
                          );
                          if (props.onComplete)
                            props.onComplete(true, c, additionalAudience);
                          props.close();
                        })
                        .finally(() => {
                          resetState();
                        });
                    })
                    .catch(() => {
                      if (props.onComplete) props.onComplete(false);
                    });
                } else if (
                  props.notation.notationType === NotationType.DataInsight
                ) {
                  DataInsightService.addComment(
                    props.notation.id,
                    action as IAction,
                    convertBeaconRteToHtml(details),
                    assignedTo,
                    methodOfContact,
                  )
                    .then(() => {
                      DataInsightService.getComments(props.notation.id)
                        .then(c => {
                          c.sort(
                            (a, b) =>
                              b.createdOn.getTime() - a.createdOn.getTime(),
                          );
                          if (props.onComplete) props.onComplete(true, c);
                          props.close();
                        })
                        .finally(() => {
                          resetState();
                        });
                    })
                    .catch(() => {
                      if (props.onComplete) props.onComplete(false);
                    });
                }
              } else {
                setIsSubmitting(false);
              }
            } catch {
              setIsSubmitting(false);
            }
          }}
          color="primary"
          variant="contained"
        >
          {props.usedToLower ? 'Lower Alert' : 'Add Response'}
        </Button>
      </DialogActions>
      {isSubmitting && <LinearProgress />}
    </Dialog>
  );
};

export default NotationResponseDialogV2;
